"use client";
import { useState, useEffect } from 'react';

// Icons
const BuildingIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-600"><rect x="4" y="2" width="16" height="20" rx="2" ry="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5v14" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="m19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>;

export default function CompaniesPage() {
    const [mounted, setMounted] = useState(false);
    const [companies, setCompanies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState<any>(null);
    const [formData, setFormData] = useState({ company_name: '', full_name: '' });

    const fetchCompanies = async () => {
        try {
            const res = await fetch('/api/companies');
            const data = await res.json();
            if (Array.isArray(data)) {
                setCompanies(data);
            } else {
                setCompanies([]);
            }
        } catch (error) { 
            console.error(error); 
            setCompanies([]);
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { 
        setMounted(true);
        fetchCompanies(); 
    }, []);

    if (!mounted) return null;

    const handleEdit = (company: any) => {
        setEditingCompany(company);
        setFormData({ company_name: company.company_name, full_name: company.full_name || '' });
        setIsFormOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this company?')) return;
        try {
            await fetch(`/api/companies?id=${id}`, { method: 'DELETE' });
            fetchCompanies();
        } catch (error) { console.error(error); }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const method = editingCompany ? 'PUT' : 'POST';
            const body = editingCompany
                ? { ...formData, company_id: editingCompany.company_id }
                : formData;

            await fetch('/api/companies', {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            setFormData({ company_name: '', full_name: '' });
            setEditingCompany(null);
            setIsFormOpen(false);
            fetchCompanies();
        } catch (error) { console.error(error); }
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingCompany(null);
        setFormData({ company_name: '', full_name: '' });
    };

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto" suppressHydrationWarning>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Companies</h1>
                    <p className="text-slate-500 text-sm">Manage organizations in your system</p>
                </div>
                <button
                    onClick={() => setIsFormOpen(true)}
                    className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition flex items-center gap-2 active:scale-95"
                >
                    <PlusIcon /> <span className="hidden md:inline">Add Company</span> <span className="md:hidden">Add</span>
                </button>
            </div>

            {/* Form Modal */}
            {isFormOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-slate-800">{editingCompany ? 'Edit Company' : 'New Company'}</h2>
                            <button onClick={handleCloseForm} className="text-slate-400 hover:text-slate-600 p-2">✕</button>
                        </div>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase ml-1">Company Name (Short)</label>
                                <input
                                    type="text"
                                    value={formData.company_name}
                                    onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))}
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                    required
                                    placeholder="e.g. ABC Ltd"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase ml-1">Full Name</label>
                                <input
                                    type="text"
                                    value={formData.full_name}
                                    onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))}
                                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                                    placeholder="e.g. ABC Services Private Limited"
                                />
                            </div>
                            <div className="flex gap-3 mt-2">
                                <button type="button" onClick={handleCloseForm} className="flex-1 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition">Cancel</button>
                                <button type="submit" className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition">
                                    {editingCompany ? 'Update' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
                {loading ? (
                    [1, 2, 3].map(i => <div key={i} className="h-48 bg-slate-200 rounded-2xl animate-pulse" />)
                ) : companies.map((c) => (
                    <div key={c.company_id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition group flex flex-col">
                        <div className="flex items-start justify-between mb-4">
                            <div className="p-3 bg-indigo-50 rounded-xl group-hover:bg-indigo-100 transition delay-75">
                                <BuildingIcon />
                            </div>
                            <span className="text-xs font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded-lg">ID: {c.company_id}</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-1">{c.company_name}</h3>
                        <p className="text-slate-500 text-sm leading-relaxed flex-1 mb-4">{c.full_name || "No full name provided."}</p>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => handleEdit(c)}
                                className="flex-1 bg-indigo-50 text-indigo-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-indigo-100 transition flex items-center justify-center gap-1"
                            >
                                <EditIcon /> Edit
                            </button>
                            <button
                                onClick={() => handleDelete(c.company_id)}
                                className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition flex items-center justify-center gap-1"
                            >
                                <TrashIcon /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            {(!loading && companies.length === 0) && (
                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-200">
                    <div className="flex justify-center mb-4 opacity-20"><BuildingIcon /></div>
                    <p className="text-slate-400 font-medium">No companies found yet.</p>
                    <button
                        onClick={() => setIsFormOpen(true)}
                        className="mt-4 text-indigo-600 font-bold hover:underline"
                    >
                        Add your first company
                    </button>
                </div>
            )}
        </div>
    );
}
